import { useTranslation } from "react-i18next"; // src/portal/vendor/VendorSetupPassword.jsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RiLockPasswordLine, RiArrowRightLine, RiShieldKeyholeLine, RiCheckboxCircleLine, RiErrorWarningLine } from 'react-icons/ri';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

export default function VendorSetupPassword() {const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const id = searchParams.get('id') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError('Passwords do not match');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/portal/auth/vendor/setup-password', { id, email, password, token });
      if (res.data.success) setDone(true);else
      setError(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !id || !email) return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${isDarkMode ? 'bg-zinc-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <RiErrorWarningLine size={48} className="text-red-500 mb-4" />
      <p className="text-sm font-bold uppercase tracking-widest text-red-500">{t("invalid_or_expired_link")}</p>
    </div>);


  if (done) return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? 'bg-zinc-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`w-full max-w-md text-center p-10 border rounded-[2rem] ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} shadow-2xl`}>
        <div className="w-20 h-20 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-500/20">
          <RiCheckboxCircleLine className="text-white" size={40} />
        </div>
        <h1 className="text-2xl font-bold mb-2 tracking-tight">{t("security_established")}</h1>
        <p className="text-gray-500 font-medium mb-10">{t("your_portal_access_key_has_been_successf")}</p>
        <button onClick={() => navigate('/portal/vendor/login')} className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20">
          {t("proceed_to_login")}
        </button>
      </motion.div>
    </div>);


  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${isDarkMode ? 'bg-zinc-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-600/20">
            <RiShieldKeyholeLine size={40} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">{t("access_setup")}</h1>
          <p className={`${isDarkMode ? 'text-zinc-500' : 'text-gray-500'} font-medium`}>{t("finalize_your_credentials_for")} {email}</p>
        </div>

        <div className={`${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border rounded-[2rem] p-8 shadow-2xl`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error &&
            <div className={`p-4 ${isDarkMode ? 'bg-red-950 text-red-400 border-red-900' : 'bg-red-50 text-red-600 border-red-100'} border rounded-2xl text-xs font-bold uppercase tracking-widest flex items-center gap-3`}>
                <RiErrorWarningLine size={18} /> {error}
              </div>
            }
            
            <div className="space-y-4">
               <div>
                  <label className={`block text-[10px] font-bold ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} uppercase tracking-widest mb-2 ml-1`}>{t("new_access_key")}</label>
                  <div className="relative group">
                    <RiLockPasswordLine className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'} group-focus-within:text-red-500 transition-colors`} size={20} />
                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-12 pr-4 py-3.5 ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-2xl focus:outline-none focus:border-red-500 transition-all`} />
                  </div>
               </div>
               <div>
                  <label className={`block text-[10px] font-bold ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} uppercase tracking-widest mb-2 ml-1`}>{t("confirm_authorization")}</label>
                  <div className="relative group">
                    <RiLockPasswordLine className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'} group-focus-within:text-red-500 transition-colors`} size={20} />
                    <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className={`w-full pl-12 pr-4 py-3.5 ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-2xl focus:outline-none focus:border-red-500 transition-all`} />
                  </div>
               </div>
            </div>

            <button type="submit" disabled={loading} className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 flex items-center justify-center gap-3 active:scale-98 disabled:opacity-50 group">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{t("finalize_connection")} <RiArrowRightLine className="group-hover:translate-x-1 transition-transform" /></>}
            </button>
          </form>
        </div>
      </motion.div>
    </div>);

}